import React, { useCallback, useRef, useState, useEffect, useMemo, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { Box, Typography as Muitypo } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { LicenseManager } from 'ag-grid-enterprise';
import { CellClickedEvent, CellRendererSelectorFunc, IServerSideDatasource, GetRowIdParams } from 'ag-grid-community';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './custom.css';
import { Tabs } from '../../components';
import { GLOBAL_USER, META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import GridTopActions from './GridTopActions';
import AppliedFilters from '../VehiclesGrid/AppliedFilters';
// import { getRowData } from '../../services/vehicles';
// import VehicleListLoader from './VehicleListLoader';
import useMetaData from '../../hooks/useMetaData';
import { useAppSelector } from '../../hooks/useRedux';
import { RootState } from '../../redux/store';
import { gridOptions } from '../../utils/gridOptions';
import { metaData } from './dummy';
import { Inventory } from '../../types/inventory';
// import { fetchData } from '../VehiclesGrid/FetchData';
import AssignmentModal from '../VehiclesGrid/AssignmentModal';
import Requests from '../Requests';
import { getInventoryDetails, getInventoryFilterValues } from '../../services/inventory';
import VehicleListLoader from '../VehiclesGrid/VehicleListLoader';
import AGGridHoc from '../../hoc/AGGridHoc';
import { cellTypes } from '../Assignments/CellTypes';
import { usePermissions } from '../../hooks/usePermissions';
import { useQuickFilter } from '../../hooks/useQuickFilter';
import useFilters from '../../hooks/useFilters';
import { useDataSource } from '../../hooks/useDataSource';
import groupingFn from '../../utils/grouping';
import { ReactComponent as WebLink } from '../../assets/icons/web_link.svg';
import { ReactComponent as CheckBoxTick } from '../../assets/icons/checkbox-checked.svg';
import { ReactComponent as CheckBoxUntick } from '../../assets/icons/checkbox-unchecked.svg';
import { ELocations } from '../../types';
import { useLocationPermissions } from '../../hooks/useLocationPermissions';
import { CellTypes } from '../../services/vehicles';

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY as string);

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const IconComponent = ({ data }: any) => {
  const { UUID } = data;
  const url = `${process.env.REACT_APP_AVAILABLE_CARS_URL}?UUID=${UUID}`;
  return (
    <Box
      onClick={() => {
        navigator.clipboard.writeText(url);
      }}
    >
      <LightTooltip title="Copy" placement="bottom">
        <WebLink />
      </LightTooltip>
    </Box>
  );
};

const IconNotAvailable = () => {
  return <Box> -- </Box>;
};

const IconCheckBoxTickedComponent = ({ data }: any) => {
  return (
    <Box display="flex" alignItems="center">
      <LightTooltip title="" placement="bottom">
        <CheckBoxTick />
      </LightTooltip>
    </Box>
  );
};

const IconCheckBoxNotTickedComponent = () => {
  return (
    <Box display="flex" alignItems="center">
      <LightTooltip title="" placement="bottom">
        <CheckBoxUntick />
      </LightTooltip>
    </Box>
  );
};

const InventoryGrid = () => {
  const { hasLocationPermissions } = useLocationPermissions();
  const [gridApi, setGridApi] = useState<any>(null);
  const perPage = 50;
  const getRowId = useCallback((params: any) => {
    return Math.random().toString();
  }, []);
  const [filterModel, setFilterModel] = useState<{ [key: string]: any }>();
  const gridRef = useRef<AgGridReact>(null);
  const [inputText, setInputText] = useState();
  const [showSearchBar, setShowSearchBar] = useState(true);
  const { filteredVins, setFilteredVins } = useQuickFilter();
  const [toCompareVehicle, setToCompareVehicle] = useState<any>(null);
  const [groupBy, setGroupBy] = useState<any>();
  const appliedFilter = useSelector((state: RootState) => state.filter.appliedFilter);
  const [updateInventory, setUpdateInventory] = useState(false);
  const areFiltersApplied = filterModel && !!Object.keys(filterModel).length;
  const gridHeight = areFiltersApplied ? '225pt' : '190pt';

  const [paginationInfo, setPaginationInfo] = useState({
    sort: '',
    sortStatus: false,
    filter: '',
    filterOp: [],
  });
  const [uiHeadings, setUIHeadings] = useState([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [active, setActive] = useState(0);

  let tempData: any = [];
  useEffect(() => {
    if (pathname.includes('my-requests')) {
      setActive(1);
    }
  }, [pathname]);

  const getFilterValuesAsync = async (params: any) => {
    const col = params.colDef.field;
    const data = await getInventoryFilterValues(col);
    return params.success(data.data);
  };

  const filterParamsTemp = {
    values: getFilterValuesAsync,
  };

  const batchNoFilterParamsTemp = {
    filterOptions: ['inRange'],
    maxNumConditions: 1,
  };

  const columnDefs = useMemo(
    () =>
      metaData?.columnDefs?.map(
        ({
          type,
          headerName,
          field,
          sortable,
          width,
          filter,
          hide,
          valueGetter,
          suppressFiltersToolPanel,
          suppressColumnsToolPanel,
          enableRowGroup,
          ...rest
        }: any) => {
          const cellRenderer = (CellTypes as any)[type];

          const cellRendererSelector: CellRendererSelectorFunc<Inventory> = (params) => {
            const isCountryUS = params.data?.country === 'United States';

            if (field === 'subCategory') {
              const titleStatus: any = params.data?.titleStatus;
              const isItUsed = !['new', ''].includes(titleStatus?.toLowerCase());
              if (isItUsed) {
                return {
                  component: cellRenderer,
                  params: {
                    sx: {
                      pointerEvents: 'none',
                    },
                    disabled: isItUsed,
                  },
                };
              }
              const isNotAvailable = params.data?.subCategory === 'Requested' || params.data?.subCategory === 'On Hold';
              return {
                component: cellRenderer,
                params: {
                  sx: {
                    color: !isNotAvailable ? '#456BD9' : null,
                  },
                  disabled: isNotAvailable,
                },
              };
            }

            if (field === 'weblink') {
              const isNotAvailable =
                params.data?.subCategory === 'Requested' ||
                params.data?.subCategory === 'On Hold' ||
                (params.data?.originalCategory === 'Inventory' && params.data?.originalSubCategory === 'Internal');

              return {
                component: !isNotAvailable && isCountryUS ? IconComponent : IconNotAvailable,
              };
            }
            if (field === 'inTransitToLocation' && params.data?.inTransitToLocation !== undefined) {
              return {
                component:
                  params.data?.inTransitToLocation === 'true' || params.data?.inTransitToLocation.includes('true')
                    ? IconCheckBoxTickedComponent
                    : IconCheckBoxNotTickedComponent,
              };
            }
            return {
              component: cellRenderer,
            };
          };

          if (field === 'euPortDate') {
            if (!hasLocationPermissions([ELocations.EUROPE])) {
              return {
                hide: true,
                filter: false,
                suppressColumnsToolPanel: true,
              };
            }
          }

          return {
            colId: field,
            headerName,
            field,
            sortable,
            width,
            filter,
            tooltipField: field,
            cellRendererSelector,
            cellRenderer,
            hide,
            valueGetter,
            suppressFiltersToolPanel,
            suppressColumnsToolPanel,
            enableRowGroup,
            //   hide: appliedFilter?.columns ? !appliedFilter?.columns.includes(field) : false,
            filterParams: field === 'batchNumber' ? batchNoFilterParamsTemp : filterParamsTemp,
            ...rest,
          };
        }
      ),
    []
  );

  let tempGroupedData: any = [];

  const datasource = {
    getRows(params: any) {
      // console.log(JSON.stringify(params.request, null, 1));
      const { startRow, endRow, rowGroupCols, sortModel, groupKeys, filterModel: paramFilter } = params.request;
      /* Pagination */
      const page = endRow && endRow / 50;

      /* Sorting */
      const sort = `${sortModel[0]?.sort}:${sortModel[0]?.colId}`;
      const sortStatus = sortModel?.length > 0;

      setPaginationInfo((prevData) => ({
        ...prevData,
        sort,
        sortStatus,
      }));

      /* Sidebar Filter */
      let tempFilter = {
        temp: { values: [], filterType: 'set', filter: '', operator: '', conditions: [], filterTo: '' },
      };
      const tempFilterKeys: any = [];
      const filterOpCheck: any = [];
      if (paramFilter) {
        tempFilter = paramFilter;
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(tempFilter)) {
          if (key === 'batchNumber') {
            const repl = (s: any) => {
              return s.toString().replace(/\d+/g, function (m: any) {
                return '000000000'.substring(m.length - 1) + m;
              });
            };

            const temp = `batchNumber:${repl(value?.filter)},${repl(value?.filterTo)}`;
            filterOpCheck.push('batchNumber:RANGE');
            tempFilterKeys.push(temp);
          } else {
            if (value && value?.filter) {
              const temp = `${key}:${value?.filter}`;
              tempFilterKeys.push(temp);
            }
            if (value && value?.operator) {
              const conditions = value?.conditions?.map((a: any) => a?.filter);
              const temp = `${key}:${conditions}`;
              filterOpCheck.push(`${key}:${value?.operator}`);
              tempFilterKeys.push(temp);
            }
            if (value && value?.values?.length > 0) {
              let valArr: any = value?.values;
              if (key.includes('Date')) {
                valArr = valArr.map((x: any) => {
                  const d = new Date(x);
                  /* eslint-disable prefer-template */
                  return [d.getFullYear(), ('0' + (d.getMonth() + 1)).slice(-2), ('0' + d.getDate()).slice(-2)].join(
                    '-'
                  );
                });
              }
              const temp = `${key}:${valArr}`;
              tempFilterKeys.push(temp);
            }
          }
        }
      }

      setPaginationInfo((prevData) => ({
        ...prevData,
        filter: tempFilterKeys,
        filterOp: filterOpCheck,
      }));

      /* Grouping */
      const grouping = rowGroupCols?.slice(-1)[0]?.field;
      let groupByData;
      if (groupKeys.length > 0) {
        const myArr = groupKeys[0]?.split('(')[0];
        groupByData = `${grouping}:${myArr}`;
      }

      if (grouping && page > 1 && groupKeys.length === 0) {
        const numberOfItemsPerPage = 50;
        const start = (page - 1) * numberOfItemsPerPage + 1;
        const end = start + numberOfItemsPerPage;
        const tempNewAllRowData: any = tempGroupedData.slice(start, end);
        let arr: any = [];
        if (grouping && groupKeys.length === 0) {
          arr = groupingFn(grouping, tempNewAllRowData);
        }

        params.successCallback(arr, tempGroupedData.length);
      } else {
        getInventoryDetails(50, page, sortStatus, sort, inputText, grouping, groupKeys, tempFilterKeys, filterOpCheck)
          .then((newAllRowData: any) => {
            const lastRow = newAllRowData?.totalData ? newAllRowData?.totalData : newAllRowData?.data?.length;
            let arr = newAllRowData?.data;
            if (grouping && page === 1) {
              tempGroupedData = newAllRowData?.data;
            }
            if (grouping && groupKeys.length === 0) {
              arr = groupingFn(grouping, newAllRowData?.data);
            }
            tempData = arr;
            params.successCallback(arr, lastRow);
          })
          .catch(() => {
            params.failCallback();
          });
      }
    },
  };

  const setHeadings = (columns: any) => {
    const headings = columns?.filter((column: any) => !column.hide)?.map((col: any) => col.field.replace('.', '_'));
    setUIHeadings(headings);
  };

  const onGridReady = (params: any) => {
    gridRef.current?.api.closeToolPanel();
    setGridApi(params);
    setHeadings(params?.api?.getColumnDefs());
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridApi) {
      onGridReady(gridApi);
    }
  }, [inputText]);

  useEffect(() => {
    if (groupBy) {
      gridApi?.columnApi?.setRowGroupColumns([groupBy]);
    }
  }, [groupBy]);

  useEffect(() => {
    gridRef?.current?.api?.setFilterModel(appliedFilter?.filters);
  }, [appliedFilter?.filters]);

  const onFilterChanged = (event: any) => {
    setFilterModel(event.api.getFilterModel());
  };

  const onPaginationChanged = (params: any) => {
    // if (gridApi) {
    //   columnDefs.map((column) => {
    //     console.log(column);
    //     const ageFilter = gridApi?.api?.getFilterInstance(column?.field);
    //     console.log(ageFilter?.refreshFilterValues);
    //     return ageFilter?.refreshFilterValues();
    //   });
    // }
  };

  const onToggleToolPanelClick = useCallback((panelId: 'columns' | 'filters') => {
    if (gridRef.current?.api.getOpenedToolPanel() === panelId) {
      gridRef.current?.api.closeToolPanel();
    } else {
      gridRef.current?.api.openToolPanel(panelId);
    }
  }, []);

  const onSearch = useCallback(() => {
    // if (gridApi) {
    //   onGridReady(gridApi);
    // }
  }, []);

  const closeFilter = () => {
    gridRef.current?.api.closeToolPanel();
  };

  const onColumnRowGroupChanged = (event: any) => {
    if (event.columns && event.columns.slice(-1)[0]?.rowGroupActive) {
      setGroupBy(event.columns.slice(-1)[0]?.colId);
    } else if (event.columns.slice(-1)[0]?.rowGroupActive === false) {
      setGroupBy('');
    }
  };

  const getInventory = async () => {
    setShowSearchBar(true);
    setUpdateInventory(true);
  };

  const handleStatusClick = (event: CellClickedEvent<Inventory, Inventory['subCategory']>) => {
    const titleStatus: any = event?.data?.titleStatus.toLowerCase();
    if (event.value === 'Available' && ['new', ''].includes(titleStatus)) {
      setToCompareVehicle(event.data);
    }
  };

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    console.log('eve', event);
    if (event.colDef.colId === 'subCategory') {
      handleStatusClick(event);
    }
  }, []);

  if (!columnDefs?.length) {
    return <VehicleListLoader />;
  }

  return (
    <>
      {!showSearchBar && (
        <Muitypo variant="h6" component="h1" flexGrow={1} color="#fff" marginTop="15px">
          .
        </Muitypo>
      )}
      {showSearchBar && (
        <Box className="top-section">
          <GridTopActions
            onSearch={onSearch}
            onToggleToolPanelClick={onToggleToolPanelClick}
            filteredVins={filteredVins}
            setInputText={setInputText}
            inputText={inputText}
            setFilteredVins={setFilteredVins}
            paginationInfo={paginationInfo}
            uiHeadings={uiHeadings}
          />
        </Box>
      )}
      <Tabs
        active={active}
        tabItems={[
          {
            label: 'Inventory Vehicles',
            onClick() {
              setShowSearchBar(true);
              setActive(0);
              navigate('/v360-app/inventory');
            },
            content: (
              <>
                {areFiltersApplied && (
                  <AppliedFilters filterModel={filterModel} gridRef={gridRef} columnDefs={columnDefs} />
                )}
                <Box width="100%" height={`calc(100vh - ${gridHeight})`} className="ag-theme-material">
                  <AGGridHoc gridRef={gridRef}>
                    <AgGridReact
                      {...gridOptions}
                      rowModelType="serverSide"
                      onFilterChanged={onFilterChanged}
                      getRowId={getRowId}
                      columnDefs={columnDefs}
                      onGridReady={onGridReady}
                      cacheBlockSize={perPage}
                      pagination
                      paginationPageSize={perPage}
                      paginateChildRows
                      ref={gridRef}
                      maxConcurrentDatasourceRequests={1}
                      sideBar
                      maxBlocksInCache={1}
                      onPaginationChanged={onPaginationChanged}
                      onCellFocused={closeFilter}
                      onColumnRowGroupChanged={onColumnRowGroupChanged}
                      onCellClicked={onCellClicked}
                      onColumnVisible={(e) => setHeadings(gridApi?.api?.getColumnDefs())}
                    />
                  </AGGridHoc>
                </Box>
              </>
            ),
          },
          {
            label: 'My Requests',
            onClick() {
              setShowSearchBar(false);
              setActive(1);
              navigate('/v360-app/inventory/my-requests');
            },
            content: <Requests />,
          },
        ]}
      />
      <AssignmentModal
        show={toCompareVehicle !== null}
        onHide={() => setToCompareVehicle(null)}
        toCompareVehicle={toCompareVehicle}
        updateInventory={getInventory}
      />
    </>
  );
};

export default InventoryGrid;
